import React, { useState, useMemo } from "react";
import { Dropdown, Button, Collapse } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import CompletedIcon from "../../../icons/svg/completed.svg";
import CurrentIcon from "../../../icons/svg/current.svg";
import SkippedIcon from "../../../icons/svg/skipped.svg";
import NextIcon from "../../../icons/svg/tick.svg";
import "./OpportunityStatusDrilldown.css";

const { Panel } = Collapse;

/**
 * Custom collapsible tree component for opportunity status selection with 3-level hierarchy
 * Solves width issues by using collapsible panels instead of cascading menus
 */
export const OpportunityStatusDrilldown = ({
  leadStatusList,
  canAddEditOpportunity,
  opportunityStatusLoader,
  value,
  onChange,
  statusHistory = [],
  showStatusIcons = false,
  size = "small",
  className = "",
  displayRender,
}) => {
  const [open, setOpen] = useState(false);
  const [activeKeys, setActiveKeys] = useState([]);

  // Build hierarchical tree structure
  const statusTree = useMemo(() => {
    if (!leadStatusList || leadStatusList.length === 0) return [];

    const idMap = {};
    leadStatusList.forEach(item => {
      idMap[item.id] = {
        ...item,
        children: [],
      };
    });

    const tree = [];
    leadStatusList.forEach(item => {
      if (item.parentStatusId) {
        const parent = idMap[item.parentStatusId];
        if (parent) {
          parent.children.push(idMap[item.id]);
        } else {
          tree.push(idMap[item.id]);
        }
      } else {
        tree.push(idMap[item.id]);
      }
    });

    return tree;
  }, [leadStatusList]);

  // Get status icon and class for nodes - matching cascader logic exactly
  const getStatusDisplay = statusNode => {
    if (!showStatusIcons) return null;

    let iconSrc;
    let iconAlt;
    let statusClass = "";

    // Find the statusHistory object for this statusNode by checking:
    // 1. Direct match with newStatus.id
    // 2. Match with parentStatus.id
    // 3. Match with parentStatus.parentStatus.id (and so on up the hierarchy)
    const statusHistoryObject = statusHistory.find(history => {
      if (!history.newStatus) return false;

      // Check direct match
      if (+history.newStatus.id === +statusNode.id) return true;

      // Check parent hierarchy
      let { parentStatus } = history.newStatus;
      while (parentStatus) {
        if (+parentStatus.id === +statusNode.id) return true;
        ({ parentStatus } = parentStatus);
      }

      return false;
    });

    if (!statusHistoryObject) {
      // Not found in statusHistory - mark as Upcoming
      iconSrc = NextIcon;
      iconAlt = "Upcoming";
      statusClass = "text-gray-400";
    } else if (statusHistoryObject.isTransferred === true) {
      // If isTransferred = true then mark as Skipped
      iconSrc = SkippedIcon;
      iconAlt = "Skipped";
      statusClass = "text-orange-500";
    } else {
      // Get the top parentStatus of the current status (value = currentStatusId)
      const currentStatusEntry = statusHistory.find(entry => +entry.newStatus?.id === +value);

      if (currentStatusEntry?.newStatus) {
        // Find the top parent of current status
        let topParent = currentStatusEntry.newStatus;
        while (topParent.parentStatus) {
          topParent = topParent.parentStatus;
        }

        // Check if this statusNode is the top parent of current status OR is the current status itself
        if (+topParent.id === +statusNode.id || +statusHistoryObject.newStatus.id === +value) {
          // Mark as Current
          iconSrc = CurrentIcon;
          iconAlt = "Current";
          statusClass = "text-blue-600 font-semibold";
        } else {
          // Mark as Completed
          iconSrc = CompletedIcon;
          iconAlt = "Completed";
          statusClass = "text-green-600";
        }
      } else {
        // Fallback to Upcoming if current status not found
        iconSrc = NextIcon;
        iconAlt = "Upcoming";
        statusClass = "text-gray-400";
      }
    }

    return { iconSrc, iconAlt, statusClass };
  };

  // Find the current selected status name
  const selectedStatusName = useMemo(() => {
    if (!value || !leadStatusList) return "Select Status";
    const status = leadStatusList.find(s => +s.id === +value);
    return status?.name || "Select Status";
  }, [value, leadStatusList]);

  const handleSelect = (node, e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(node.id);
    }
    setOpen(false);
    setActiveKeys([]);
  };

  const renderStatusIcon = statusDisplay => {
    if (!statusDisplay) return null;

    return (
      <div className="tree-status-icon">
        {statusDisplay.iconSrc ? (
          <img src={statusDisplay.iconSrc} alt={statusDisplay.iconAlt} />
        ) : (
          <div className="tree-icon-placeholder" />
        )}
      </div>
    );
  };

  const renderTreeNode = (node, level = 0, isParentLevel = true) => {
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = +value === +node.id;
    // Only show status icons on top-level parent nodes (level 0, regardless of whether they have children)
    const shouldShowIcon = showStatusIcons && isParentLevel;
    const statusDisplay = shouldShowIcon ? getStatusDisplay(node) : null;

    if (hasChildren) {
      // Parent node with children - render as collapsible panel
      return (
        <Panel
          key={`panel-${node.id}`}
          header={
            <div className="tree-panel-header">
              {shouldShowIcon && renderStatusIcon(statusDisplay)}
              <span className={`tree-node-name ${statusDisplay?.statusClass || ""}`}>
                {node.name}
              </span>
            </div>
          }
          className="tree-collapse-panel"
        >
          <div className="tree-panel-content">
            {node.children.map(child => {
              const childHasChildren = child.children && child.children.length > 0;
              if (childHasChildren) {
                // Nested collapse for child with children
                return (
                  <Collapse
                    key={`collapse-${child.id}`}
                    bordered={false}
                    expandIcon={({ isActive }) => (
                      <RightOutlined rotate={isActive ? 90 : 0} className="tree-expand-icon" />
                    )}
                    className="tree-nested-collapse"
                  >
                    {renderTreeNode(child, level + 1, false)}
                  </Collapse>
                );
              }
              // Leaf child node - clickable, no icon
              const isChildSelected = +value === +child.id;
              return (
                <div
                  key={child.id}
                  className={`tree-leaf-node ${isChildSelected ? "selected" : ""}`}
                  onClick={e => handleSelect(child, e)}
                >
                  <span className="tree-node-name">{child.name}</span>
                </div>
              );
            })}
          </div>
        </Panel>
      );
    }

    // Top-level leaf node without children - clickable item with icon
    if (isParentLevel) {
      return (
        <div
          key={node.id}
          className={`tree-leaf-node ${isSelected ? "selected" : ""}`}
          onClick={e => handleSelect(node, e)}
        >
          {shouldShowIcon && renderStatusIcon(statusDisplay)}
          <span className={`tree-node-name ${statusDisplay?.statusClass || ""}`}>{node.name}</span>
        </div>
      );
    }

    // Child leaf node - clickable item, no icon
    return (
      <div
        key={node.id}
        className={`tree-leaf-node ${isSelected ? "selected" : ""}`}
        onClick={e => handleSelect(node, e)}
      >
        <span className="tree-node-name">{node.name}</span>
      </div>
    );
  };

  const dropdownContent = (
    <div className="tree-dropdown-content">
      <Collapse
        bordered={false}
        activeKey={activeKeys}
        onChange={keys => setActiveKeys(keys)}
        expandIcon={({ isActive }) => (
          <RightOutlined rotate={isActive ? 90 : 0} className="tree-expand-icon" />
        )}
        className="tree-collapse"
      >
        {statusTree.map(node => renderTreeNode(node))}
      </Collapse>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      disabled={!canAddEditOpportunity || opportunityStatusLoader}
      overlay={dropdownContent}
      trigger={["click"]}
      placement="bottomLeft"
      overlayClassName="opportunity-status-tree-overlay"
    >
      <Button
        size={size}
        className={`opportunity-status-tree-button ${className}`}
        loading={opportunityStatusLoader}
        disabled={!canAddEditOpportunity}
      >
        {displayRender ? (
          displayRender([selectedStatusName])
        ) : (
          <span className="tree-button-text">{selectedStatusName}</span>
        )}
        <DownOutlined className="tree-button-arrow" />
      </Button>
    </Dropdown>
  );
};

OpportunityStatusDrilldown.displayName = "OpportunityStatusDrilldown";
